import { Field, Form, Formik, FormikProps } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import FileUpload from '../../components/FileUpload/FileUpload';
import Button from '../../components/v2/Button/Button';
import Stepper from '../../components/v2/Stepper';
import { Typography } from '../../components/v2/Typography/Typography';
import { countriesList, usaStates } from '../../data/states-cities';
import { CustomFormikInput } from '../Modules/friendsAndFamily/CustomFormikInput';
import { CustomFormikSelect } from '../Modules/friendsAndFamily/CustomFormikSelect';

export type DocumentSupportType =
  | 'certificate_wet_signature'
  | 'letter_of_good_standing'
  | 'notarized_or_state_specific';

export type PCOFormValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  shippingAddress: string;
  shippingAddress2: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  shippingCountry: string;
  documentSupportTypes: DocumentSupportType[];
  documentUploadUrl: string | null;
  documentUploadFile: File | null;
  uploadedFilename: string | null;
};

type Props = {
  initialValues: PCOFormValues;
  onSubmit: (
    values: PCOFormValues,
    initialValues: PCOFormValues
  ) => Promise<void> | void;
  submitting?: boolean;
};

const TOTAL_STEPS = 3;

const stepSchemas: Record<number, Yup.AnyObjectSchema> = {
  1: Yup.object().shape({
    firstName: Yup.string().trim().required('First name is required'),
    middleName: Yup.string(),
    lastName: Yup.string().trim().required('Last name is required'),
  }),
  2: Yup.object().shape({
    shippingAddress: Yup.string().trim().required('Address is required'),
    shippingAddress2: Yup.string(),
    shippingCity: Yup.string().trim().required('City is required'),
    shippingState: Yup.string().trim().required('State is required'),
    shippingZip: Yup.string().trim().required('Zip/Post code is required'),
    shippingCountry: Yup.string().trim().required('Country is required'),
  }),
  3: Yup.object().shape({
    documentSupportTypes: Yup.array()
      .of(
        Yup.string().oneOf([
          'certificate_wet_signature',
          'letter_of_good_standing',
          'notarized_or_state_specific',
        ])
      )
      .min(1, 'Please select at least one document type'),
    documentUploadUrl: Yup.string()
      .nullable()
      .when('documentSupportTypes', {
        is: (types: DocumentSupportType[] | undefined) =>
          Array.isArray(types) && types.includes('notarized_or_state_specific'),
        then: (schema) =>
          schema.test(
            'has-upload',
            'Please upload the document you need assistance with',
            function (value) {
              const { documentUploadFile } = this.parent as PCOFormValues;
              return Boolean(value || documentUploadFile);
            }
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    documentUploadFile: Yup.mixed().nullable(),
  }),
};

const isStepValid = async (step: number, values: PCOFormValues) => {
  try {
    await stepSchemas[step].validate(values, { abortEarly: false });
    return true;
  } catch {
    return false;
  }
};

const documentTypeOptions: Array<{
  value: DocumentSupportType;
  label: string;
}> = [
  {
    value: 'certificate_wet_signature',
    label: 'Certificate w/ wet signature and raised seal',
  },
  {
    value: 'letter_of_good_standing',
    label: 'Letter of Good Standing w/ wet signature and raised seal',
  },
  {
    value: 'notarized_or_state_specific',
    label: 'Notarized or State Specific Documents from county clerk',
  },
];

const PCOForm = ({ initialValues, onSubmit, submitting = false }: Props) => {
  const [step, setStep] = useState(1);
  const [isEditingName, setIsEditingName] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  const goBack = () => {
    setStepError(null);
    setStep((s) => Math.max(s - 1, 1));
  };

  return (
    <div className='container flex flex-col justify-center max-w-md px-4 sm:px-0 py-4'>
      <div className='mb-6'>
        <Stepper step={step} steps={TOTAL_STEPS} />
      </div>

      <Formik<PCOFormValues>
        initialValues={initialValues}
        enableReinitialize={false}
        validateOnMount={false}
        onSubmit={(values) => {
          void onSubmit(values, initialValues);
        }}
      >
        {(formik: FormikProps<PCOFormValues>) => {
          const isLastStep = step === TOTAL_STEPS;

          const handlePrimary = async () => {
            setStepError(null);
            const valid = await isStepValid(step, formik.values);
            if (!valid) {
              // Force all step fields to show validation errors
              const fields = Object.keys(stepSchemas[step].fields);
              const touched: Record<string, boolean> = {};
              fields.forEach((f) => (touched[f] = true));
              formik.setTouched({ ...formik.touched, ...touched });
              await formik.validateForm();
              setStepError('Please fill in all required fields before continuing.');
              return;
            }
            if (isLastStep) {
              formik.submitForm();
            } else {
              setStep((s) => s + 1);
            }
          };

          return (
            <Form className='w-full'>
              {step === 1 && (
                <div>
                  <Typography type='display-600'>
                    Confirm your legal name
                  </Typography>
                  <Typography
                    type='body-400'
                    variant='functional-low'
                    className='mt-2'
                  >
                    This is how your name will appear on your official
                    documents.
                  </Typography>

                  {!isEditingName ? (
                    <div className='mt-8'>
                      <Typography type='display-400'>
                        {[
                          formik.values.firstName,
                          formik.values.middleName,
                          formik.values.lastName,
                        ]
                          .filter(Boolean)
                          .join(' ') || '—'}
                      </Typography>
                      <Button
                        variant='text'
                        size='400'
                        type='button'
                        className='mt-2'
                        onClick={() => setIsEditingName(true)}
                      >
                        Edit
                      </Button>
                    </div>
                  ) : (
                    <div className='mt-6'>
                      <Field
                        name='firstName'
                        component={CustomFormikInput}
                        label='First Name'
                        placeholder='Enter your first name'
                        required={true}
                        helperText={
                          formik.touched.firstName && formik.errors.firstName
                        }
                        error={
                          formik.touched.firstName &&
                          Boolean(formik.errors.firstName)
                        }
                      />
                      <Field
                        name='middleName'
                        component={CustomFormikInput}
                        label='Middle Name'
                        placeholder='Enter your middle name (optional)'
                      />
                      <Field
                        name='lastName'
                        component={CustomFormikInput}
                        label='Last Name'
                        placeholder='Enter your last name'
                        required={true}
                        helperText={
                          formik.touched.lastName && formik.errors.lastName
                        }
                        error={
                          formik.touched.lastName &&
                          Boolean(formik.errors.lastName)
                        }
                      />
                      <Button
                        variant='text'
                        type='button'
                        className='mt-2'
                        onClick={() => {
                          formik.setFieldValue('firstName', initialValues.firstName);
                          formik.setFieldValue('middleName', initialValues.middleName);
                          formik.setFieldValue('lastName', initialValues.lastName);
                          formik.setFieldTouched('firstName', false);
                          formik.setFieldTouched('middleName', false);
                          formik.setFieldTouched('lastName', false);
                          setStepError(null);
                          setIsEditingName(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div>
                  <Typography type='display-600'>
                    Where would you like your documents shipped?
                  </Typography>
                  <div className='mt-6'>
                    <Field
                      name='shippingAddress'
                      component={CustomFormikInput}
                      label='Address'
                      placeholder='Street address'
                      required={true}
                      helperText={
                        formik.touched.shippingAddress &&
                        formik.errors.shippingAddress
                      }
                      error={
                        formik.touched.shippingAddress &&
                        Boolean(formik.errors.shippingAddress)
                      }
                    />
                    <Field
                      name='shippingAddress2'
                      component={CustomFormikInput}
                      label='Address line 2'
                      placeholder='Apartment, suite, etc. (optional)'
                    />
                    <Field
                      name='shippingCity'
                      component={CustomFormikInput}
                      label='City/Town'
                      placeholder='City'
                      required={true}
                      helperText={
                        formik.touched.shippingCity &&
                        formik.errors.shippingCity
                      }
                      error={
                        formik.touched.shippingCity &&
                        Boolean(formik.errors.shippingCity)
                      }
                    />
                    <Field
                      name='shippingState'
                      component={CustomFormikSelect}
                      label='State/Region/Province'
                      placeholder='Select a state'
                      required={true}
                      options={usaStates.states.map((s) => ({
                        value: s.name,
                        label: s.name,
                      }))}
                      helperText={
                        formik.touched.shippingState &&
                        formik.errors.shippingState
                      }
                      error={
                        formik.touched.shippingState &&
                        Boolean(formik.errors.shippingState)
                      }
                    />
                    <Field
                      name='shippingZip'
                      component={CustomFormikInput}
                      label='Zip/Post code'
                      placeholder='Zip'
                      required={true}
                      helperText={
                        formik.touched.shippingZip && formik.errors.shippingZip
                      }
                      error={
                        formik.touched.shippingZip &&
                        Boolean(formik.errors.shippingZip)
                      }
                    />
                    <Field
                      name='shippingCountry'
                      component={CustomFormikSelect}
                      label='Country'
                      placeholder='Select a country'
                      required={true}
                      options={countriesList.map((c) => ({
                        value: c.name,
                        label: c.name,
                      }))}
                      helperText={
                        formik.touched.shippingCountry &&
                        formik.errors.shippingCountry
                      }
                      error={
                        formik.touched.shippingCountry &&
                        Boolean(formik.errors.shippingCountry)
                      }
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <Typography type='display-600'>
                    What type of document support do you need?
                  </Typography>
                  <Typography
                    type='body-400'
                    variant='functional-low'
                    className='mt-2'
                  >
                    Many States require additional paperwork when filling out a
                    marriage license. We suggest you double-check county
                    requirements before submitting this request.
                  </Typography>
                  <Typography
                    type='body-200'
                    variant='functional-low'
                    className='mt-2'
                  >
                    Select any that apply.
                  </Typography>
                  <div className='mt-6 flex flex-col gap-3'>
                    {documentTypeOptions.map((opt) => {
                      const checked =
                        formik.values.documentSupportTypes.includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className={`flex flex-row items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                            checked
                              ? 'border-forest-primary bg-cosmicLatte-dark'
                              : 'border-forest-100'
                          }`}
                        >
                          <input
                            type='checkbox'
                            name='documentSupportTypes'
                            value={opt.value}
                            checked={checked}
                            onChange={() => {
                              const next = checked
                                ? formik.values.documentSupportTypes.filter(
                                    (v) => v !== opt.value
                                  )
                                : [
                                    ...formik.values.documentSupportTypes,
                                    opt.value,
                                  ];
                              formik.setFieldValue(
                                'documentSupportTypes',
                                next
                              );
                              formik.setFieldTouched(
                                'documentSupportTypes',
                                true,
                                false
                              );
                            }}
                          />
                          <Typography type='body-400'>{opt.label}</Typography>
                        </label>
                      );
                    })}
                  </div>
                  {formik.touched.documentSupportTypes &&
                    formik.errors.documentSupportTypes && (
                      <Typography
                        type='body-200'
                        className='mt-2 text-red-600'
                      >
                        {String(formik.errors.documentSupportTypes)}
                      </Typography>
                    )}

                  {formik.values.documentSupportTypes.includes(
                    'notarized_or_state_specific'
                  ) && (
                    <div className='mt-6'>
                      <Typography
                        type='body-400'
                        variant='functional-high'
                        className='mb-3'
                      >
                        Please upload the state-specific document that you need
                        filled out and/or notarized.
                      </Typography>
                      <FileUpload
                        deferUpload
                        value={formik.values.documentUploadUrl}
                        pendingFile={formik.values.documentUploadFile}
                        filename={formik.values.uploadedFilename}
                        onChange={(next) => {
                          if (!next) {
                            formik.setFieldValue('documentUploadUrl', null);
                            formik.setFieldValue('documentUploadFile', null);
                            formik.setFieldValue('uploadedFilename', null);
                          } else if ('file' in next) {
                            formik.setFieldValue('documentUploadUrl', null);
                            formik.setFieldValue('documentUploadFile', next.file);
                            formik.setFieldValue(
                              'uploadedFilename',
                              next.filename
                            );
                          } else {
                            formik.setFieldValue('documentUploadUrl', next.url);
                            formik.setFieldValue('documentUploadFile', null);
                            formik.setFieldValue(
                              'uploadedFilename',
                              next.filename
                            );
                          }
                          formik.setFieldTouched(
                            'documentUploadUrl',
                            true,
                            false
                          );
                        }}
                      />
                      {formik.touched.documentUploadUrl &&
                        formik.errors.documentUploadUrl && (
                          <Typography
                            type='body-200'
                            className='mt-2 text-red-600'
                          >
                            {String(formik.errors.documentUploadUrl)}
                          </Typography>
                        )}
                    </div>
                  )}
                </div>
              )}

              {stepError && (
                <Typography
                  type='body-200'
                  className='mt-4 text-red-600'
                >
                  {stepError}
                </Typography>
              )}

              <div className='mt-8 flex flex-row justify-between gap-4'>
                {step > 1 ? (
                  <Button variant='text' type='button' onClick={goBack}>
                    Back
                  </Button>
                ) : (
                  <span />
                )}
                <Button
                  type='button'
                  onClick={handlePrimary}
                  disabled={submitting}
                  showLoader={submitting && isLastStep}
                >
                  {isLastStep ? 'Submit' : 'Next'}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default PCOForm;
